<script>
    import { onMount } from "svelte";

    import MenuIcon from "@propublica/pp-v5-shared/src/icons/menu.svg";
    import SearchIcon from "@propublica/pp-v5-shared/src/icons/search.svg";
    import Logo from "@propublica/pp-v5-shared/src/icons/propublica-logo.svg";
    // Modal
    import CloseIcon from "@propublica/pp-v5-shared/src/icons/close.svg";
    import FacebookIcon from "@propublica/pp-v5-shared/src/icons/facebook.svg";
    import InstagramIcon from "@propublica/pp-v5-shared/src/icons/instagram.svg";
    import TwitterIcon from "@propublica/pp-v5-shared/src/icons/twitter.svg";
    // Topic Icons
    import TopicRacialJusticeIcon from "@propublica/pp-v5-shared/src/icons/topic-racial-justice.svg";
    import TopicHealthcareIcon from "@propublica/pp-v5-shared/src/icons/topic-healthcare.svg";
    import TopicPoliticsIcon from "@propublica/pp-v5-shared/src/icons/topic-politics.svg";
    import TopicCriminalJusticeIcon from "@propublica/pp-v5-shared/src/icons/topic-criminal-justice.svg";
    import NewsletterSignup from "../NewsletterSignup";
    import Button from "../Button";

    /** How much of the site nav to show. Use "minimal" over full-bleed content.
     * @type {( 'full' | 'compact' | 'minimal' )}
     */
    export let type = "full";

    /** When the Fixed nav should appear. Accepts a querySelector string and calculates its position and height on the page.
     * @type { string }
     */
    export let fixedNavScrollStart = "";

    /** @type { HTMLElement } */
    let scrollStartSiteHeaderEl;

    let scrollY = 0;
    let isOverlayOpen = false;
    let fixedNavScrollStartY = 55;

    let breakpoint = "xs";

    onMount(() => {
        function setBreakpoint() {
            if (window.matchMedia("(min-width: 0)").matches) {
                breakpoint = "xs";
            }
            if (window.matchMedia("(min-width: 36em)").matches) {
                breakpoint = "sm";
            }
            if (window.matchMedia("(min-width: 48em)").matches) {
                breakpoint = "md";
            }
            if (window.matchMedia("(min-width: 60em)").matches) {
                breakpoint = "lg";
            }
            if (window.matchMedia("(min-width: 78em)").matches) {
                breakpoint = "xl";
            }
            if (window.matchMedia("(min-width: 88em)").matches) {
                breakpoint = "xxl";
            }
        }
        window.addEventListener("resize", setBreakpoint);
        setBreakpoint();

        // Update fixedNavScrollStartY to be the height of SiteHeader or fixedNavScrollStart element.
        function updateFixedNavStart() {
            let el;
            try {
                el = document.querySelector(fixedNavScrollStart);
            } catch (error) {
                el = scrollStartSiteHeaderEl;
            }
            const top = el.getBoundingClientRect().top + window.pageYOffset;
            const height = el.offsetHeight;
            fixedNavScrollStartY = top + height;
        }
        window.addEventListener("resize", updateFixedNavStart);
        updateFixedNavStart();
    });

    function openOverlay() {
        isOverlayOpen = true;
    }

    function closeOverlay() {
        isOverlayOpen = false;
    }

    function handleKeydown(event) {
        const key = event.key || event.keyCode;
        if (key === "Escape" || key === "Esc" || key === 27) {
            if (isOverlayOpen === true) {
                closeOverlay();
            }
        }
    }
</script>

<a class="site-header-skip-link a11y a11y-focusable--absolute" href="#main"
    >Skip to content</a
>

<svelte:window bind:scrollY on:keydown={handleKeydown} />

{#if type === "minimal"}
    <div class="site-header--minimal" bind:this={scrollStartSiteHeaderEl}>
        <div class="variant-minimal__inner-wrap">
            <div class="variant-minimal__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--black)"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="minimal-menu-button"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-minimal__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                font="serif"
                bgColor="var(--red-50)"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                Donate
            </Button>
        </div>
    </div>
    <div
        class="site-header--fixed"
        class:site-header--fixed-has-scrolled={scrollY > fixedNavScrollStartY}
        aria-hidden="true"
    >
        <div class="variant-fixed__inner-wrap">
            <div class="variant-fixed__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--black)"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="minimal-menu-button-in-fixed-nav"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-fixed__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                bgColor="var(--red-50)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                Donate
            </Button>
        </div>
    </div>
{:else if type === "compact"}
    <div
        class="site-header--compact"
        class:site-header--compact-has-scrolled={scrollY >=
            fixedNavScrollStartY}
        bind:this={scrollStartSiteHeaderEl}
    >
        <div
            class="variant-compact__inner-wrap"
            class:variant-compact__inner-wrap--has-border={scrollY < 55}
        >
            <div class="variant-compact__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--black)"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="compact-menu-button"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-compact__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                bgColor="var(--red-50)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                Donate
            </Button>
        </div>
    </div>
{:else if type === "full"}
    <div class="site-header--compact hide-above-lg">
        <div
            class="variant-compact__inner-wrap"
            class:variant-compact__inner-wrap--has-border={scrollY <= 0}
        >
            <div class="variant-compact__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--black)"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="full-menu-button-on-mobile"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-compact__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                bgColor="var(--red-50)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                buttonSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale2)"
                    : "var(--scale3)"}
            >
                Donate
            </Button>
        </div>
    </div>
    <div
        class="site-header--full hide-below-lg"
        bind:this={scrollStartSiteHeaderEl}
    >
        <div class="section-logo">
            <a class="wordmark" href="https://www.propublica.org">
                <Logo />
            </a>

            <p class="tagline">
                <strong>Investigative Journalism in the Public Interest</strong>
            </p>
        </div>

        <div class="section-menu">
            <Button
                bgColor="var(--site-header-button-bg-color)"
                textColor="var(--site-header-button-color)"
                on:click={openOverlay}
                dataLink="Menu"
                dataTestID="full-menu-button-on-desktop"
            >
                <MenuIcon slot="iconBefore" />
                Menu
            </Button>

            <Button
                bgColor="var(--site-header-button-bg-color)"
                textColor="var(--site-header-button-color)"
                iconOnly="true"
                shape="circle"
                on:click={() => {
                    isOverlayOpen = true;
                    document.querySelector(".search-form__input-text").focus();
                }}
                dataLink="Search"
                dataTestID="search-button"
            >
                <SearchIcon slot="iconBefore" />
                Search
            </Button>
        </div>

        <div class="section-donate">
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                bgColor="var(--red-50)"
                font="serif"
                textSize={breakpoint === "xs" ||
                breakpoint === "sm" ||
                breakpoint === "md"
                    ? "var(--scale-2)"
                    : ""}
                horizontalSpacing="var(--scale3)"
            >
                Donate
            </Button>
        </div>

        <hr class="site-header--full__divider" />

        <div class="section-links">
            <ul class="links-list">
                <li>
                    <a
                        class="link link--featured"
                        href="https://www.propublica.org/series/the-secret-irs-files"
                        >The Secret IRS Files</a
                    >
                </li>
                <li>
                    <a
                        class="link"
                        href="https://www.propublica.org/local-initiatives/"
                        >Local Initiatives</a
                    >
                </li>
                <li>
                    <a
                        class="link"
                        href="https://www.propublica.org/newsletters/"
                        >Newsletters</a
                    >
                </li>
                <li>
                    <a class="link" href="https://www.propublica.org/about/"
                        >About Us</a
                    >
                </li>
            </ul>
        </div>

        <div class="section-newsletter">
            <NewsletterSignup
                newsletterList="The Big Story"
                placeholderText="Get the Big Story newsletter"
                inputID="site-header-signup"
                placement="main-nav"
            />
        </div>
    </div>
    <div
        class="site-header--fixed hide-below-lg"
        class:site-header--fixed-has-scrolled={scrollY > fixedNavScrollStartY}
        aria-hidden="true"
    >
        <div class="variant-fixed__inner-wrap">
            <div class="variant-fixed__section-menu">
                <Button
                    bgColor="transparent"
                    textColor="var(--black)"
                    iconOnly
                    on:click={openOverlay}
                    dataLink="Menu"
                    dataTestID="full-menu-button-in-fixed-nav"
                >
                    <MenuIcon slot="iconBefore" />
                    Menu
                </Button>
            </div>
            <a
                class="variant-fixed__wordmark"
                href="https://www.propublica.org"
            >
                <Logo />
            </a>
            <Button
                elementType="a"
                href="https://www.propublica.org/donate"
                bgColor="var(--red-50)"
                font="serif"
            >
                Donate
            </Button>
        </div>
    </div>
{/if}

<div
    class="site-header-overlay"
    class:site-header-overlay--is-open={isOverlayOpen}
    data-testid="overlay"
>
    <div class="variant-overlay__top-inner-wrap">
        <div class="variant-overlay__section-menu">
            <Button
                bgColor="transparent"
                iconSize="var(--scale0)"
                textColor="white"
                iconOnly
                on:click={closeOverlay}
                dataLink="Menu"
                dataTestID="close-overlay-button"
            >
                <CloseIcon slot="iconBefore" />
                Close
            </Button>
        </div>
        <a class="variant-overlay__wordmark" href="https://www.propublica.org">
            <Logo />
        </a>
        <Button
            elementType="a"
            href="https://www.propublica.org/donate"
            bgColor="var(--red-50)"
            font="serif"
            textSize={breakpoint === "xs" ||
            breakpoint === "sm" ||
            breakpoint === "md"
                ? "var(--scale-2)"
                : ""}
            buttonSize={breakpoint === "xs" ||
            breakpoint === "sm" ||
            breakpoint === "md"
                ? "var(--scale2)"
                : "var(--scale3)"}
            dataSkip
        >
            Donate
        </Button>
    </div>

    <div
        class="site-header-overlay__wrap-expanded"
        data-pp-location="masthead wrap-expanded"
    >
        <!-- Search -->

        <div class="site-header__section-search">
            <form
                class="search-form"
                role="search"
                action="https://propublica.org/search"
                method="get"
            >
                <label
                    class="a11y hed-form hed-site-search"
                    for="search-form__input-text">Search ProPublica:</label
                >
                <div class="search-form__input-wrap">
                    <input
                        placeholder="Search"
                        id="search-form__input-text"
                        class="search-form__input-text"
                        type="search"
                        value=""
                        name="qss"
                    />
                    <Button
                        bgColor="var(--cool-70)"
                        buttonSize="var(--scale4)"
                        iconOnly="true"
                        iconSize="var(--scale0)"
                        shape="circle"
                        textColor="white"
                    >
                        <SearchIcon slot="iconBefore" />
                        Search
                    </Button>
                </div>
            </form>
        </div>
        <!-- end .site-header__section-search -->

        <!-- Links expanded -->

        <nav
            class="site-header__section-links-expanded"
            aria-label="Expanded Links"
            hidden
        >
            <!-- Topics -->

            <div class="site-header__links site-header__links-topics">
                <strong class="site-header__links-heading">Topics</strong>
                <ul class="site-header__links-list">
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Racial Justice"
                            href="https://www.propublica.org/topics/racial-justice"
                        >
                            <span class="icon">
                                <TopicRacialJusticeIcon />
                            </span>
                            Racial Justice
                        </a>
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Health Care"
                            href="https://www.propublica.org/topics/health-care"
                        >
                            <span class="icon">
                                <TopicHealthcareIcon />
                            </span>
                            Health Care
                        </a>
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Politics"
                            href="https://www.propublica.org/topics/politics"
                        >
                            <span class="icon">
                                <TopicPoliticsIcon />
                            </span>
                            Politics
                        </a>
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Criminal Justice"
                            href="https://www.propublica.org/topics/criminal-justice"
                        >
                            <span class="icon">
                                <TopicCriminalJusticeIcon />
                            </span>
                            Criminal Justice
                        </a>
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="more…"
                            href="https://www.propublica.org/topics/">more…</a
                        >
                    </li>
                </ul>
            </div>
            <!--  end .site-header__links-topics -->

            <!-- Browse by Place -->

            <div class="site-header__links site-header__links-region">
                <strong class="site-header__links-heading"
                    >Browse by Place</strong
                >
                <ul class="site-header__links-list">
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Midwest"
                            href="https://www.propublica.org/midwest/"
                            >Midwest</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Southwest"
                            href="https://www.propublica.org/southwest/"
                            >Southwest</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="South"
                            href="https://www.propublica.org/south/">South</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Texas"
                            href="https://www.propublica.org/texas/">Texas</a
                        >
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-region -->

            <!-- Browse by Type -->

            <div class="site-header__links site-header__links-type">
                <strong class="site-header__links-heading"
                    >Browse by Type</strong
                >
                <ul class="site-header__links-list">
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Graphics & Data"
                            href="https://www.propublica.org/newsapps/"
                            >Graphics & Data</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Newsletters"
                            href="https://www.propublica.org/newsletters/"
                            >Newsletters</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Series"
                            href="https://www.propublica.org/series/">Series</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Videos"
                            href="https://www.propublica.org/video/">Videos</a
                        >
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-type -->

            <!-- Special -->

            <div class="site-header__links site-header__links-special">
                <ul class="site-header__links-list">
                    <!-- <li>
                        <a class="site-header__link site-header__link-featured" data-link="Featured Link" href="https://www.propublica.org/">Featured Link</a>
                    </li> -->
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Local Reporting Network"
                            href="https://www.propublica.org/local-reporting-network/"
                            >Local Reporting Network</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Electionland"
                            href="https://www.propublica.org/electionland/"
                            >Electionland</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Data Store"
                            href="https://www.propublica.org/datastore/"
                            >Data Store</a
                        >
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-special -->

            <!-- Info -->

            <div class="site-header__links site-header__links-info">
                <strong class="site-header__links-heading">Info</strong>
                <ul class="site-header__links-list">
                    <li>
                        <a
                            class="site-header__link"
                            data-link="About Us"
                            href="https://www.propublica.org/about/">About Us</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Impact"
                            href="https://www.propublica.org/impact/">Impact</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Jobs & Fellowships"
                            href="https://www.propublica.org/jobs/"
                            >Jobs & Fellowships</a
                        >
                    </li>
                    <li>
                        <a
                            class="site-header__link"
                            data-link="Contact Us"
                            href="https://www.propublica.org/contact/"
                            >Contact Us</a
                        >
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-info -->

            <!-- Follow -->

            <div class="site-header__links site-header__links-follow">
                <strong class="site-header__links-heading">Follow</strong>
                <ul
                    class="site-header__links-list site-header__links-list-follow"
                    data-link="Facebook"
                >
                    <li>
                        <Button
                            elementType="a"
                            href="https://www.facebook.com/propublica"
                            iconOnly="true"
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <FacebookIcon slot="iconBefore" />
                            Facebook
                        </Button>
                    </li>
                    <li data-link="Twitter">
                        <Button
                            elementType="a"
                            href="https://twitter.com/propublica"
                            iconOnly="true"
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <TwitterIcon slot="iconBefore" />
                            Twitter
                        </Button>
                    </li>
                    <li data-link="Instagram">
                        <Button
                            elementType="a"
                            href="https://www.instagram.com/propublica/"
                            iconOnly="true"
                            textColor="white"
                            shape="circle"
                            bgColor="var(--cool-70)"
                        >
                            <InstagramIcon slot="iconBefore" />
                            Instagram
                        </Button>
                    </li>
                </ul>
            </div>
            <!-- end .site-header__links-follow -->
        </nav>
        <!-- end .site-header__section-links-expanded -->
    </div>
    <!-- end .site-header-overlay__wrap-expanded -->
</div>

<style lang="scss">
    @import "@propublica/pp-v5-shared/src/css/config/typography.vars";
    @import "@propublica/pp-v5-shared/src/css/config/spacing";
    @import "@propublica/pp-v5-shared/src/css/config/colors";
    @import "@propublica/pp-v5-shared/src/css/config/column-settings";
    @import "@propublica/pp-v5-shared/src/css/config/column-setter";

    @import "@propublica/pp-v5-shared/src/css/utils/dark-mode.mixins";

    .site-header--minimal,
    .site-header--compact,
    .site-header--full,
    .site-header--fixed,
    .site-header-overlay {
        :global(.text-dark) &,
        & {
            @include set-light-dark(
                --header-light,
                var(--cool-10),
                var(--cool-80)
            );
            @include set-light-dark(
                --header-dark,
                var(--cool-80),
                var(--cool-10)
            );
        }
        :global(.text-light) & {
            @include set-light-dark(
                --header-light,
                var(--cool-80),
                var(--cool-80)
            );
            @include set-light-dark(
                --header-dark,
                var(--cool-10),
                var(--cool-10)
            );
        }
        --header-border: 1px solid var(--header-light);
        --header-hover: var(--blue-50);
        color: var(--header-dark);
        font-family: var(--fonts-sans);
        line-height: var(--line-height-2);
    }

    a {
        color: currentColor;
    }

    .site-header-skip-link {
        @include set-light-dark(
            background-color,
            var(--gray-10),
            var(--gray-70)
        );
        background-color: var(--gray-10);
        border-radius: 4px;
        display: block;
        font-family: var(--fonts-sans);
        font-size: var(--scale-2);
        font-weight: 700;
        line-height: var(--line-height-2);
        padding: var(--spacing-1);
        text-decoration: none;

        left: 1rem;
        top: 1rem;
        z-index: 1001;

        &:focus {
            outline-offset: 4px;
            border: none;
        }
    }

    .site-header--minimal,
    .site-header--compact,
    .site-header--fixed {
        // border-bottom: var(--header-border);
        width: 100%;
    }

    .site-header--minimal {
        --site-header-page-margins: var(
            --spacing1
        ); // This is meant to match with --opener-page-margins
        @include breakpoint-min(md) {
            --site-header-page-margins: var(--spacing3);
        }
        position: absolute;
    }

    .site-header--compact,
    .site-header--fixed {
        @include set-light-dark(background-color, var(--white), var(--black));
        position: sticky;
        top: 0;
        z-index: 1000;

        @supports (backdrop-filter: saturate(150%) blur(20px)) or
            (--webkit-backdrop-filter: saturate(150%) blur(20px)) {
            backdrop-filter: saturate(150%) blur(20px);
            -webkit-backdrop-filter: saturate(150%) blur(20px);
            // prettier-ignore
            :global(.text-dark) &, & { @include set-light-dark(--header-bg, rgba(255, 255, 255, 0.8), rgba(27, 33, 39, 0.8)); background-color: var(--header-bg); } // This is not quite ideal since the rgba value here can not refer to a css var
            // prettier-ignore
            :global(.text-light) & { @include set-light-dark(--header-bg, rgba(27, 33, 39, 0.8), rgba(27, 33, 39, 0.8)); background-color: var(--header-bg); } // This is not quite ideal since the rgba value here can not refer to a css var
        }
    }

    .site-header--fixed {
        position: fixed;
        top: 0;
        transform: translateY(-150%);
        transition: 0.2s;
        border-bottom: var(--header-border);
    }

    .site-header--fixed-has-scrolled {
        transform: translateY(0%);
    }

    .variant-minimal__inner-wrap,
    .variant-compact__inner-wrap,
    .variant-fixed__inner-wrap,
    .variant-overlay__top-inner-wrap {
        align-items: center;
        display: grid;
        font-size: var(--scale-2);
        grid-template-areas: "menu logo donate";
        padding: var(--spacing-1) 0;

        @include breakpoint-min(sm) {
            font-size: var(--scale-1);
        }
    }

    /*
    For the compact site-header, we apply the bottom border in two different places based on scroll position:
    - Before someone has scrolled, it’s applied on an inner div so that the border spans the nav contents.
    - After someone has scrolled, it’s applied on the outer div so that the border spans the full browser viewport.
*/

    .site-header--compact-has-scrolled {
        border-bottom: var(--header-border);
    }

    .variant-compact__inner-wrap--has-border {
        border-bottom: var(--header-border);
    }

    .variant-minimal__inner-wrap {
        grid-template-columns: 5em 1fr 5em;
        margin: 0 var(--site-header-page-margins);

        @include breakpoint-min(md) {
            --site-header-page-margins: var(--spacing3);
        }
        @include breakpoint-min(lg) {
            grid-template-columns: 2rem 1fr 5em;
        }
    }

    @include grid(variant-compact__inner-wrap);
    @include grid(variant-fixed__inner-wrap);
    @include grid(variant-overlay__top-inner-wrap);

    .variant-compact__inner-wrap,
    .variant-fixed__inner-wrap,
    .variant-overlay__top-inner-wrap {
        grid-template-columns: 5em 1fr 5em;
    }

    .variant-minimal__section-menu,
    .variant-compact__section-menu,
    .variant-fixed__section-menu,
    .variant-overlay__section-menu {
        grid-area: menu;
        margin-left: -0.75rem; // A bit of layout adjustment to the menu button so that the icon lines up with the border
    }

    .variant-minimal__wordmark,
    .variant-compact__wordmark,
    .variant-fixed__wordmark,
    .variant-overlay__wordmark {
        display: block;
        fill: currentColor;
        grid-area: logo;
        justify-self: center;
        margin: 0 var(--spacing-1);
        max-width: 13em;
        width: 100%;
    }

    .variant-minimal__wordmark {
        justify-self: center;

        @include breakpoint-min(lg) {
            justify-self: start;
        }
    }

    @include grid(site-header--full);

    .site-header--full {
        align-items: center;
        border: none;
        display: grid;
        font-size: var(--scale-1);
        grid-template-areas:
            "menu   logo   logo       donate"
            "border border border     border"
            "links  links  newsletter newsletter";
        grid-template-columns: 13em 1fr 7em 13em;
        padding: var(--spacing2) 0;
        row-gap: var(--spacing-1);
    }

    .section-menu {
        grid-area: menu;
        padding-bottom: var(--spacing0);
        justify-self: start;

        display: flex;
        flex-direction: row;
        gap: var(--spacing0);

        @include set-light-dark(
            --site-header-button-bg-color,
            var(--cool-10),
            var(--cool-80)
        );
        @include set-light-dark(
            --site-header-button-color,
            var(--cool-80),
            var(--cool-10)
        );
    }

    .site-header--full .section-logo {
        align-items: center;
        display: flex;
        flex-direction: column;
        grid-area: logo;
        justify-content: center;
        justify-self: stretch;
        padding: 0 0 var(--spacing0);
    }

    .site-header--full .wordmark {
        display: block;
        max-width: 25em;
        width: 100%;

        &:hover {
            color: var(--header-hover);
        }
    }

    .section-donate {
        grid-area: donate;
        justify-self: end;
        padding-bottom: var(--spacing0);
    }

    .site-header--full__divider {
        background-color: var(--header-light);
        border: none;
        grid-area: border;
        height: 1px;
        margin: 0;
        width: 100%;
    }

    .section-links {
        grid-area: links;
    }

    .links-list {
        align-items: center;
        display: flex;
        flex-direction: row;

        gap: var(--spacing0);

        @include breakpoint-min(xl) {
            gap: var(--spacing2);
        }
    }

    .link {
        text-decoration: none;
        font-size: var(--scale-2);
        font-weight: 700;
        transition: 0.2s;

        &:hover {
            color: var(--blue-50);
        }
    }

    .link--featured {
        color: var(--white); /* TODO: use mixins */
        background-color: var(--green-50);
        padding: 0.3em 0.4em;
    }

    .link--featured:hover {
        color: var(--white); /* TODO: use mixins */
        background-color: var(--green-50);
        opacity: 0.75;
    }

    .section-newsletter {
        border-left: var(--header-border);
        // display: block;
        grid-area: newsletter;
        justify-self: stretch;
        padding-left: var(--spacing0);
        // position: relative;
    }

    .tagline {
        display: block;
        font-family: var(--fonts-serif);
        font-size: var(--scale-2);
        font-weight: 700;
        margin-top: var(--spacing-2);
        text-align: center;
    }

    // -------------------------
    // OVERLAY
    // -------------------------

    .site-header-overlay {
        :global(.text-dark) &,
        & {
            @include set-light-dark(
                --header-light,
                var(--cool-70),
                var(--cool-70)
            );
            @include set-light-dark(
                --header-dark,
                var(--cool-10),
                var(--cool-10)
            );
        }
        :global(.text-light) & {
            @include set-light-dark(
                --header-light,
                var(--cool-70),
                var(--cool-70)
            );
            @include set-light-dark(
                --header-dark,
                var(--cool-10),
                var(--cool-10)
            );
        }
        background: var(--cool-90);
        color: var(--cool-10);
        bottom: 0;
        left: 0;
        overflow: scroll;
        position: fixed;
        right: 0;
        top: 0;
        z-index: 1000;

        opacity: 0;
        transition: opacity 0.2s;
        pointer-events: none;
    }

    .site-header-overlay--is-open {
        opacity: 1;
        pointer-events: auto;
    }

    @include grid(site-header-overlay__wrap-expanded);

    .site-header-overlay__wrap-expanded {
        align-items: center;
        display: flex;
        flex-direction: column;
        padding-bottom: var(--spacing2);

        @include breakpoint-min(md) {
            padding-bottom: var(--spacing3);
        }
        @include breakpoint-min(lg) {
            margin-top: 5rem;
        }
    }

    // -------------------------
    // SEARCH
    // -------------------------

    .site-header__section-search {
        width: 100%;
    }

    .search-form {
        font-size: var(--scale0);
        padding: var(--spacing1) 0 var(--spacing3);

        @include breakpoint-min(md) {
            padding-top: 0;
        }
    }

    .search-form__input-wrap {
        align-items: center;
        display: flex;
        justify-content: center;
    }

    .search-form__input-text {
        background: var(--header-light);
        border: none;
        border-radius: 0.2em;
        color: var(--header-dark);
        font-family: var(--fonts-sans);
        font-size: inherit;
        padding: 0.5em;
        margin-right: var(--spacing-1);
        max-width: 20em;
        width: 100%;

        &::placeholder {
            color: inherit;
        }
    }

    // ----------------------------------------
    // LINKS
    // ----------------------------------------

    .site-header__section-links-expanded {
        column-gap: var(--spacing0);
        display: grid;
        grid-template-areas:
            "special border1 region"
            "topics border1 type"
            "info border1 follow";
        grid-template-columns: 1fr 1px 1fr;
        row-gap: var(--spacing1);
        @include breakpoint-min(md) {
            column-gap: var(--spacing1);
            grid-template-areas:
                "special border1 region border2 info"
                "topics border1 type border2 follow";
            grid-template-columns: 1fr 1px 1fr 1px 1fr;
        }

        &::before,
        &::after {
            background: var(--header-light);
            display: block;
        }
        &::before {
            content: "";
            grid-area: border1;
        }
        &::after {
            @include breakpoint-min(md) {
                content: "";
                grid-area: border2;
            }
        }
    }

    // Link subsection heading styles
    .site-header__links-heading {
        display: block;
        font-size: var(--scale0);
        font-weight: bold;
        margin-bottom: var(--spacing1);
    }

    // Link list styles
    .site-header__links-list {
        display: block;
        font-size: var(--scale-1);
        > * {
            margin-bottom: var(--spacing0);
            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    // Link styles
    .site-header__link {
        text-decoration: none;

        &:focus,
        &:hover {
            color: var(--header-hover);
        }
    }

    // Link subsections that get a top border
    // below the md breakpoint
    .site-header__links-topics,
    .site-header__links-type,
    .site-header__links-info,
    .site-header__links-follow {
        &::before {
            background: var(--header-light);
            content: "";
            display: block;
            height: 1px;
            margin-bottom: var(--spacing1);
            width: 50%;
        }
    }

    // Link subsections that lose the top border
    // above the md breakpoint
    .site-header__links-info {
        &::before {
            @include breakpoint-min(md) {
                display: none;
            }
        }
    }

    // Link subsection styles
    .site-header__links-topics {
        grid-area: topics;

        .icon {
            margin-right: var(--spacing-2);
            vertical-align: -0.1em;
        }
    }
    .site-header__links-region {
        grid-area: region;
    }
    .site-header__links-type {
        grid-area: type;
    }
    .site-header__links-special {
        font-weight: bold;
        grid-area: special;

        .site-header__link {
            font-size: var(--scale0);
        }
    }
    .site-header__links-info {
        grid-area: info;
        order: -1;
    }
    .site-header__links-follow {
        grid-area: follow;
    }
    .site-header__links-list-follow {
        display: flex;
        flex-wrap: wrap;
        font-size: var(--scale0);

        > * {
            margin-right: var(--spacing-1);
            margin-top: 0;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    // Featured link overrides
    // .site-header__link--featured {
    //     background: var(--orange-50);
    //     color: var(--white);
    //     padding: 0.3em 0.4em;

    //     &:focus,
    //     &:hover {
    //         background: var(--orange-40);
    //         color: var(--white);
    //     }
    // }
</style>
